/**
 * Carries a notification link's destination across the sign-in round-trip.
 *
 * A `/n/:type/:id` link arrives by email, usually on a device with no session —
 * that is the common case, not the edge case. `PrivateRoute` already preserves
 * the path for a *refresh* (`/auth/reauth#prevPath=…`), but when the refresh
 * fails the user is sent to the public app to sign in properly, and that hop
 * loses the destination. Without this the link's whole job — getting someone to
 * the offer they were emailed about — fails at the last step.
 *
 * sessionStorage rather than a `?next=` parameter: the trip leaves this origin
 * for the public app and comes back to `/auth/callback` here, and sessionStorage
 * is keyed per origin per tab, so it survives that. The alternative would mean
 * threading a parameter through the public app's login page and all four MFA
 * screens.
 *
 * Dies with the tab, and is consumed once. Signing in from a *different* tab
 * than the one holding the link is the known gap — that lands on the dashboard.
 */

const DESTINATION_KEY = 'codezo_notification_destination';

/** Only ever our own in-app paths — never a value that could redirect off-site. */
function isSafeInAppPath(path: string): boolean {
	return path.startsWith('/') && !path.startsWith('//');
}

export function rememberNotificationDestination(path: string): void {
	if (!isSafeInAppPath(path)) return;
	try {
		sessionStorage.setItem(DESTINATION_KEY, path);
	} catch {
		// Private-mode/quota failures are non-fatal — the user lands on the
		// dashboard instead of the notification's target.
	}
}

/** Reads and clears in one go, so a stale destination can't hijack a later login. */
export function takeNotificationDestination(): string | null {
	try {
		const path = sessionStorage.getItem(DESTINATION_KEY);
		sessionStorage.removeItem(DESTINATION_KEY);
		return path && isSafeInAppPath(path) ? path : null;
	} catch {
		return null;
	}
}
